import {GriffelStyle, makeStyles, mergeClasses} from "@fluentui/react-components";
import {HTMLAttributes, useEffect} from "react";
import {StatePair, useStatePair} from "../../../type/States";

const useStyles = makeStyles({
    row: {
        display: 'flex',
        flexDirection: 'row',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
})

export type LayoutAlignment = 'center' | 'start' | 'end'

export type LayoutUnit = '4px' | '8px' | '12px' | '16px' | '20px' | '24px'

//region justify-content
const layoutJustifyContentStyles: Record<LayoutAlignment, GriffelStyle> = {
    center: {
        justifyContent: 'center'
    },
    start: {
        justifyContent: 'start'
    },
    end: {
        justifyContent: 'end'
    }
}

const useLayoutJustifyContentStyles = makeStyles(layoutJustifyContentStyles)
//endregion

//region align-items
const layoutAlignItemsStyles: Record<LayoutAlignment, GriffelStyle> = {
    center: {
        alignItems: 'center'
    },
    start: {
        alignItems: 'start'
    },
    end: {
        alignItems: 'end'
    }
}

const useLayoutAlignItemsStyles = makeStyles(layoutAlignItemsStyles)
//endregion

//region gap
const layoutGapStyles: Record<LayoutUnit, GriffelStyle> = {
    "4px": { gap: '4px' },
    "8px": { gap: '8px' },
    "12px": { gap: '12px' },
    "16px": { gap: '16px' },
    "20px": { gap: '20px' },
    "24px": { gap: '24px' }
}

const useLayoutGapStyles = makeStyles(layoutGapStyles)
//endregion

//region marginLeft
const layoutMarginLeftStyle: Record<LayoutUnit, GriffelStyle> = {
    "4px": { marginLeft: '4px' },
    "8px": { marginLeft: '8px' },
    "12px": { marginLeft: '12px' },
    "16px": { marginLeft: '16px' },
    "20px": { marginLeft: '20px' },
    "24px": { marginLeft: '24px' }
}

const useLayoutMarginLeftStyles = makeStyles(layoutMarginLeftStyle)
//endregion

//region marginRight
const layoutMarginRightStyle: Record<LayoutUnit, GriffelStyle> = {
    "4px": { marginRight: '4px' },
    "8px": { marginRight: '8px' },
    "12px": { marginRight: '12px' },
    "16px": { marginRight: '16px' },
    "20px": { marginRight: '20px' },
    "24px": { marginRight: '24px' }
}

const useLayoutMarginRightStyles = makeStyles(layoutMarginRightStyle)
//endregion

//region marginTop
const layoutMarginTopStyle: Record<LayoutUnit, GriffelStyle> = {
    "4px": { marginTop: '4px' },
    "8px": { marginTop: '8px' },
    "12px": { marginTop: '12px' },
    "16px": { marginTop: '16px' },
    "20px": { marginTop: '20px' },
    "24px": { marginTop: '24px' }
}

const useLayoutMarginTopStyles = makeStyles(layoutMarginTopStyle)
//endregion

//region marginBottom
const layoutMarginBottomStyle: Record<LayoutUnit, GriffelStyle> = {
    "4px": { marginBottom: '4px' },
    "8px": { marginBottom: '8px' },
    "12px": { marginBottom: '12px' },
    "16px": { marginBottom: '16px' },
    "20px": { marginBottom: '20px' },
    "24px": { marginBottom: '24px' }
}

const useLayoutMarginBottomStyles = makeStyles(layoutMarginBottomStyle)
//endregion


export type LayoutProps = HTMLAttributes<HTMLDivElement> & {
    alignItems?: LayoutAlignment
    justifyContent?: LayoutAlignment
    gap?: LayoutUnit
    marginLeft?: LayoutUnit
    marginRight?: LayoutUnit
    marginTop?: LayoutUnit
    marginBottom?: LayoutUnit
}

function useLayoutClasses(props: LayoutProps, direction: 'row' | 'column'): StatePair<string> {
    const styles = useStyles()
    const layoutJustifyContentStyles = useLayoutJustifyContentStyles()
    const layoutAlignItemsStyles = useLayoutAlignItemsStyles()
    const layoutGapStyles = useLayoutGapStyles()
    const layoutMarginLeftStyles = useLayoutMarginLeftStyles()
    const layoutMarginRightStyles = useLayoutMarginRightStyles()
    const layoutMarginTopStyles = useLayoutMarginTopStyles()
    const layoutMarginBottomStyles = useLayoutMarginBottomStyles()
    const classes = useStatePair(mergeClasses(styles[direction], props.className))

    useEffect(() => {
        classes.setValue(
            mergeClasses(
                styles[direction],
                props.alignItems === undefined ? undefined : layoutAlignItemsStyles[props.alignItems],
                props.justifyContent === undefined ? undefined : layoutJustifyContentStyles[props.justifyContent],
                props.gap === undefined ? undefined : layoutGapStyles[props.gap],
                props.marginLeft === undefined ? undefined : layoutMarginLeftStyles[props.marginLeft],
                props.marginRight === undefined ? undefined : layoutMarginRightStyles[props.marginRight],
                props.marginTop === undefined ? undefined : layoutMarginTopStyles[props.marginTop],
                props.marginBottom === undefined ? undefined : layoutMarginBottomStyles[props.marginBottom],
                props.className
            )
        )
    }, [
        direction,
        props.alignItems,
        props.justifyContent,
        props.gap,
        props.marginLeft,
        props.marginRight,
        props.marginTop,
        props.marginBottom,
        props.className
    ])

    return classes
}

function divProps(props: LayoutProps): HTMLAttributes<HTMLDivElement> {
    const { alignItems, justifyContent, gap, marginLeft, marginRight, marginTop, marginBottom, ...divProps } = props

    return divProps
}

export type RowProps = LayoutProps

export function Row(props: RowProps) {
    const layoutClasses = useLayoutClasses(props, 'row')

    return <>
        <div
            {...divProps(props)}
            className={layoutClasses.value}>
            {props.children}
        </div>
    </>
}

type ColumnProps = LayoutProps

export function Column(props: ColumnProps) {
    const layoutClasses = useLayoutClasses(props, 'column')

    return <>
        <div
            {...divProps(props)}
            className={layoutClasses.value}>
            {props.children}
        </div>
    </>
}